import { omit } from 'lodash';
import { Col } from 'antd';
import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useQuery } from 'react-query';

import Panel, { PanelProps } from '.';

import FormItem from '../form-item';
import { getProfile } from '../../../apis/users/profile';
import { getUser } from '../../../state/selectors/auth';
import { IUser } from '../../../types/models/auth';
import { IUserProfile } from '../../../types/models/user-management';
import { convertUserProfileToProfileInfoFormData } from '../../../utils/form-builder';

export enum FieldKind {
  FirstName,
  MiddleName,
  LastName,
}

export function getLayoutProps(index: number) {
  let xs;
  let md;
  switch (index) {
    case FieldKind.FirstName:
    case FieldKind.MiddleName:
    case FieldKind.LastName:
      xs = { span: 24 };
      md = { span: 8 };
      break;
  }

  return { xs, md };
}

const PanelProfile = React.memo(function PanelProfile({
  name: panelName,
  value,
  disabled,
  canPopulateFromProfile,
  onChange,
  ...props
}: PanelProps) {
  const fieldRenderer = useCallback(
    (_: string, attributes: any, error: any, Comp: React.ComponentType<any>, index = 0) => {
      const componentProps = omit(attributes, 'instructiveLabel');
      componentProps.disabled = disabled;

      // responsive layout config
      const layoutProps = getLayoutProps(index);

      return (
        <Col {...layoutProps}>
          <FormItem label={attributes.label} htmlFor={componentProps.id} error={error}>
            <Comp error={error} {...componentProps} />
          </FormItem>
        </Col>
      );
    },
    [disabled]
  );

  const userInfo: IUser | null = useSelector(getUser);
  const canFetchData = !!canPopulateFromProfile && !!userInfo && !value;
  useQuery(canFetchData && [getProfile.QUERY_KEY, { id: userInfo?.id }], () => getProfile(userInfo?.id), {
    onSuccess: (profileData: IUserProfile) => {
      if (onChange) {
        onChange({
          name: panelName,
          value: convertUserProfileToProfileInfoFormData(profileData),
        });
      }
    },
  });

  return (
    <Panel
      {...props}
      name={panelName}
      fieldRenderer={fieldRenderer}
      value={value}
      disabled={disabled}
      onChange={onChange}
    />
  );
});

export default PanelProfile;
